/**
 * ASN Documents tab — upload / list / download / delete the documents attached
 * to an ASN. Extracted from ASNDetailModal (CR-6322) to keep that file within
 * the 500-line limit; behaviour is unchanged. Owns its own document state and
 * reports the current count to the parent for the tab badge.
 */
import { useState, useEffect, useCallback } from 'react';
import { FileText, Download, Trash2 } from 'lucide-react';
import { purchasingAPI, ASNDocument, ASNDocumentType } from '../../lib/api';
import { DOCUMENT_TYPE_LABELS } from './asnModalTypes';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useASNDocumentUpload, ASN_ACCEPT_ATTR, ASN_MAX_FILE_SIZE } from './useASNDocumentUpload';
import { ASNUploadFailures } from './ASNUploadFailures';
import FileDropZone from '../common/FileDropZone';

interface ASNDocumentsTabProps {
  asnId: number;
  /** Reports the live document count so the parent can show it on the tab. */
  onCountChange?: (count: number) => void;
}

export function ASNDocumentsTab({ asnId, onCountChange }: ASNDocumentsTabProps) {
  const confirm = useConfirm();
  const { formatDate } = useFormatters();
  const [documents, setDocuments] = useState<ASNDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<ASNDocumentType>('delivery_note');

  const fetchDocuments = async () => {
    if (!asnId) return;
    try {
      setLoadingDocuments(true);
      const response = await purchasingAPI.getASNDocumentsByASN(asnId);
      const docs = response.data.documents || [];
      setDocuments(docs);
      onCountChange?.(docs.length);
    } catch (error) {
      console.error('Failed to fetch ASN documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asnId]);

  // CR-7163 (b): multi-file + drag-and-drop, shared with ASNLineDocuments.
  // The doc-type select is read at build time so every file in a batch is
  // filed under whatever type is showing when the drop happens.
  const upload = useASNDocumentUpload({
    buildFormData: useCallback(() => {
      const formData = new FormData();
      formData.append('asn', asnId.toString());
      formData.append('document_type', selectedDocType);
      return formData;
    }, [asnId, selectedDocType]),
    onUploaded: fetchDocuments,
  });

  const handleDownloadDocument = async (doc: ASNDocument) => {
    try {
      const response = await purchasingAPI.downloadASNDocument(doc.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    const ok = await confirm({ title: 'Delete Document', description: 'Are you sure you want to delete this document?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;
    try {
      await purchasingAPI.deleteASNDocument(docId);
      await fetchDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  return (
    <div className="space-y-4">
      {/* Customer review 11 Sep (drag and drop everywhere): the shared
          FileDropZone replaces the CR-7163 panel-wide handlers; a drop that
          misses it is now swallowed by the app-level guard, not opened. */}
      <div className="rounded-lg p-4 bg-muted/30 space-y-3">
        <div className="max-w-xs">
          <label className="block text-xs font-medium text-muted-foreground mb-1">Document Type</label>
          <select value={selectedDocType} onChange={(e) => setSelectedDocType(e.target.value as ASNDocumentType)} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
            {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <FileDropZone
          multiple
          accept={ASN_ACCEPT_ATTR}
          maxSize={ASN_MAX_FILE_SIZE}
          busy={upload.uploading}
          label={upload.uploading
            ? `Uploading ${Math.min(upload.progress.done + 1, upload.progress.total)} of ${upload.progress.total}...`
            : undefined}
          hint="Supported: PDF, Word, Excel, Images, ZIP (max 50MB)"
          onFiles={upload.uploadFiles}
          data-testid="asn-documents-drop-zone"
        />
        <ASNUploadFailures failed={upload.failed} onDismiss={upload.dismissFailures} />
      </div>

      {loadingDocuments ? (
        <div className="text-sm text-muted-foreground py-4 text-center">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg bg-muted/10">No documents uploaded yet</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left py-2 px-3 font-medium">Document</th>
                <th className="text-left py-2 px-3 font-medium">Type</th>
                <th className="text-left py-2 px-3 font-medium">Size</th>
                <th className="text-left py-2 px-3 font-medium">Uploaded</th>
                <th className="text-right py-2 px-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id} className="border-b last:border-b-0 hover:bg-muted/30">
                  <td className="py-2 px-3"><div className="flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><span className="font-medium truncate max-w-[200px]" title={doc.file_name}>{doc.file_name}</span></div></td>
                  <td className="py-2 px-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}</span></td>
                  <td className="py-2 px-3 text-muted-foreground">{doc.formatted_file_size || '-'}</td>
                  <td className="py-2 px-3 text-muted-foreground">
                    <div>{formatDate(doc.upload_date)}</div>
                    {doc.uploaded_by_details && <div className="text-xs">by {doc.uploaded_by_details.first_name} {doc.uploaded_by_details.last_name}</div>}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleDownloadDocument(doc)} className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors text-blue-600" title="Download"><Download className="h-4 w-4" /></button>
                      <button onClick={() => handleDeleteDocument(doc.id)} className="p-1.5 hover:bg-red-100 rounded-lg transition-colors text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
